/**
 * Kunder fra PowerOffice Go (#183 phase 3): on Prosjekter, the administrator of a company on PowerOffice
 * reads Go's customers (GET /admin/poweroffice/customers) and adds the chosen ones through the same
 * POST /admin/customers as a typed customer, one by one so one refusal does not stop the rest. One here
 * already (same org number, else name) is not offered. Nothing is written to PowerOffice.
 */
import { errorText, get, post } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, toast } from '@/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';
import { useConnected } from './useConnected';

interface GoCustomer {
  number: string | null;
  name: string;
  org_number: string | null;
  email: string | null;
  existing_id: number | null;
}
const keyOf = (c: GoCustomer) => c.number ?? c.name;

export function PowerOfficeCustomers({ sys = POWEROFFICE }: { sys?: PayrollSystem }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const connected = useConnected(sys, tenant?.payroll_system === sys.key && tenant?.me?.is_admin === true);
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: [sys.key, 'customers'],
    queryFn: async () => (await get<{ customers: GoCustomer[] }>(`${sys.base}/customers`)).customers,
    enabled: open,
    staleTime: 0,
  });
  const fresh = (q.data ?? []).filter((c) => c.existing_id == null);
  const [off, setOff] = useState<Set<string>>(new Set());
  const chosen = fresh.filter((c) => !off.has(keyOf(c)));
  const add = useMutation({
    mutationFn: async () => {
      const no: string[] = [];
      for (const c of chosen) {
        try {
          await post('/admin/customers', { name: c.name, org_number: c.org_number, contact: c.email });
        } catch (e) {
          no.push(`${c.name}: ${errorText(e)}`);
        }
      }
      return { made: chosen.length - no.length, no };
    },
    onSuccess: ({ made, no }) => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: [sys.key, 'customers'] });
      setOff(new Set());
      toast(
        no.length ? no.join(' ') : T('{n} kunder lagt til fra {system}.', { n: made, system: sys.short }),
      );
    },
    onError: (e) => toast(errorText(e)),
  });
  if (tenant?.me?.is_admin !== true || !connected) return null;
  if (!open)
    return (
      <span>
        <Button small onClick={() => setOpen(true)}>
          {T('Hent kunder fra {system}', { system: sys.short })}
        </Button>
      </span>
    );
  return (
    <section data-testid="po-customers" style={{ display: 'grid', gap: 8 }}>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter kunder fra {system} …', { system: sys.short })}
        </output>
      ) : q.isError ? (
        <span>{errorText(q.error)}</span>
      ) : fresh.length === 0 ? (
        <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
          {T('Alle kundene i {system} finnes allerede i Timefly.', { system: sys.short })}
        </span>
      ) : (
        <>
          <b style={{ fontSize: 14 }}>
            {T('Kunder i {system}, ikke i Timefly ({n})', { n: fresh.length, system: sys.short })}
          </b>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
            {fresh.map((c) => (
              <li key={keyOf(c)}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={!off.has(keyOf(c))}
                    onChange={() => {
                      const s = new Set(off);
                      if (s.has(keyOf(c))) s.delete(keyOf(c));
                      else s.add(keyOf(c));
                      setOff(s);
                    }}
                  />
                  <span>
                    {c.name}
                    {c.org_number ? `, ${c.org_number}` : ''}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <span>
            <Button tone="pri" onClick={() => add.mutate()} disabled={add.isPending || chosen.length === 0}>
              {add.isPending ? T('Legger til…') : T('Legg til {n} kunder', { n: chosen.length })}
            </Button>
          </span>
        </>
      )}
    </section>
  );
}
